import React, { useCallback } from "react";
import { Col, Row } from "antd";
import { Form, SubmitButton, Select } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { WorkflowAction, Integration, App } from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { reportService } from "/app/src/services";
import { buildParams, simpleSchemaBuilder } from "/app/src/helpers";
import HeroButton from "/app/src/components/HeroUi/Button";
/**
 * Pushes workflow action data including add, update, and remove operations for a given application.
 * @param {Object} param0 - The parameter object.
 * @param {App} param0.app - The application associated with the workflow action.
 * @param {WorkflowAction} param0.action - The workflow action to process.
 * @param {(action: WorkflowAction) => Promise<any>} param0.addWorkflowAction - Function to add a new workflow action.
 * @param {Integration[]} param0.integrations - Array of available integrations.
 * @param {(action: WorkflowAction) => Promise<any> | undefined} param0.removeWorkflowAction - Function to remove an existing workflow action.
 * @param {(action: WorkflowAction) => Promise<any>} param0.updateAction - Function to update an existing workflow action.
 * @param {boolean} param0.isNew - Indicates whether the workflow action is new.
 * @param {() => void} param0.resetSelectedApp - Function to reset the selected application.
 * @returns {JSX.Element} The rendered data push component.
 */
export default function DataPush({
  app,
  action,
  addWorkflowAction,
  integrations,
  removeWorkflowAction,
  updateAction,
  isNew,
  resetSelectedApp,
}: {
  app: App;
  action: WorkflowAction;
  addWorkflowAction: (action: WorkflowAction) => Promise<any>;
  integrations: Integration[];
  removeWorkflowAction: (action: WorkflowAction) => Promise<any> | undefined;
  updateAction: (action: WorkflowAction) => Promise<any>;
  isNew: boolean;
  resetSelectedApp: () => void;
}) {
  const { t } = useTranslation();
  const { data: reports } = useQuery({
    queryKey: ["historyReports"],
    queryFn: () => {
      return reportService.getAll(buildParams({ baseTable: "History" }));
    },
    initialData: { reports: [] },
    select: (data) => data.reports,
  });

  const removeWorkflowHandler = useCallback(() => {
    removeWorkflowAction(action);
  }, [action, removeWorkflowAction]);

  const addUpdateWorkflowHandler = useCallback(
    (values, actions) => {
      if (isNew) {
        const newAction = { ...values, appId: app.id } as WorkflowAction;
        addWorkflowAction(newAction).then((response) => {
          if (!response?.errors) {
            actions.resetForm();
            resetSelectedApp();
          }
        });
      } else {
        if (action?.id) {
          updateAction({ id: action.id, ...values }).then((response) => {
            if (!response?.errors) {
              actions.resetForm();
            }
          });
        }
      }
    },
    [isNew, app, action, addWorkflowAction, updateAction, resetSelectedApp],
  );

  return (
    <Row justify="start" gutter={16}>
      <Col span={24}>
        <Formik
          enableReinitialize
          initialValues={{
            ...(isNew
              ? { integrationId: undefined, reportId: undefined }
              : {
                  integrationId: action.integrationId,
                  reportId: action.reportId,
                }),
          }}
          validationSchema={simpleSchemaBuilder([
            { name: "integrationId", type: "number", required: true },
            { name: "reportId", type: "number", required: true },
          ])}
          onSubmit={addUpdateWorkflowHandler}
        >
          {({ dirty }) => (
            <Form>
              <Row justify="start" gutter={16}>
                <Col span={3}>
                  <span className="blockText">{t("translation:run")}</span>
                </Col>
                <Col span={8}>
                  <Form.Item name="integrationId">
                    <Select
                      name="integrationId"
                      size="large"
                      placeholder={`${t("translation:select_integration")} ${t(
                        "translation:to_run",
                      )}`}
                    >
                      {integrations.map((c) => (
                        <Select.Option key={c.id} value={c.id}>
                          {c.name} - {c.number}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="reportId">
                    <Select
                      name="reportId"
                      size="large"
                      placeholder={t("translation:select_report")}
                    >
                      {reports.map((c) => (
                        <Select.Option key={c.id} value={c.id}>
                          {c.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col flex="auto">
                  <SubmitButton
                    type="primary"
                    size="large"
                    block
                    disabled={!dirty}
                  >
                    {isNew
                      ? t("translation:add_action")
                      : t("translation:save")}
                  </SubmitButton>
                </Col>
                {!isNew && (
                  <Col span={3}>
                    <HeroButton
                      size="md"
                      variant="bordered"
                      color="default"
                      fullWidth
                      onClick={removeWorkflowHandler}
                      className="hover:border-primary-default hover:text-primary-default bg-white"
                    >
                      {t("translation:remove")}
                    </HeroButton>
                  </Col>
                )}
              </Row>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  );
}
